import { useCallback, useEffect, useMemo } from "react";
import { FeatureFlags } from "featureFlags";
import { type PerformanceWithAccountFilter } from "features/Library/Common/utils/performanceUtils";
import { GenericPerformanceListWithServerPagination } from "features/Reporting/Content/shared/GenericPerformanceListWithServerPagination";
import { type PeopleOverviewAccountsItem } from "features/Reporting/types/people";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { type ColumnOption } from "interfaces/columnOptions";
import { fetchPeopleOverviewAccountsTablePaginated, fetchPeopleOverviewAccountsTableExport } from "../utils";
import { TextTruncate } from "components";
import { Tooltip } from "components/common/tooltip";
import dateTimeUtils from "utils/dateTimeUtils";

import "features/Library/Common/utils/performanceSCSSUtils.scss";
import "../../peopleStyles.scss";
import { bindActionCreators, type Dispatch } from "@reduxjs/toolkit";
import { bindAction } from "interfaces";
import { setExportAction, setHasDataToExport } from "features/Reporting/state/export/exportSlice";
import { connect, type ConnectedProps } from "react-redux";
import { type ExportAvailability } from "features/Reporting/Content/shared/GenericPerformanceListWithServerPagination";
import { type PaginationParams } from "features/Library/Common/models";

export type Props = {
  filter: PerformanceWithAccountFilter;
  handleAccountSelection: (id: number, name: string) => void;
} & PropsFromRedux;

type AccountColumnOption = ColumnOption<PeopleOverviewAccountsItem> & { sortKey?: string };

export const AccountsTable = ({
  filter,
  handleAccountSelection,
  setExport,
  setIsExportEnabled,
  accountsExport,
}: Props) => {
  const reportEnabled = !!useFeatureFlag(FeatureFlags.PeopleReports);

  const fetchAccountsData = useCallback(
    (params: PaginationParams, signal?: AbortSignal) =>
      fetchPeopleOverviewAccountsTablePaginated(filter, params, signal),
    [filter],
  );
  const queryKey = useMemo(() => ["PeopleOverviewAccountsTable", filter] as const, [filter]);

  // Setting appropriate export method
  useEffect(() => {
    setExport({
      method: accountsExport,
      args: [filter],
    });
  }, [filter, setExport, accountsExport]);

  const updateExportDataState = useCallback(
    ({ hasDataToExport }: ExportAvailability) =>
      setIsExportEnabled({
        hasDataToExport,
        tooltipContent: "No data available for export. There are no customers with activity data.",
      }),
    [setIsExportEnabled],
  );

  const columns: AccountColumnOption[] = useMemo(
    () => [
      {
        name: "Name",
        width: 5,
        isSortable: true,
        sortKey: "AccountName",
        render(item) {
          return (
            <Tooltip
              target={
                <TextTruncate
                  className="invisibleButton"
                  onClick={() => handleAccountSelection(item.AccountId, item.AccountName)}
                >
                  {item.AccountName}
                </TextTruncate>
              }
              content={item.AccountName}
            />
          );
        },
      },
      {
        name: "Interactions",
        width: 4,
        isSortable: true,
        sortKey: "InteractionCount",
        render(item) {
          return <span>{item.InteractionCount.toLocaleString()}</span>;
        },
      },
      {
        name: "Creation date",
        width: 3,
        isSortable: true,
        sortKey: "AccountCreatedDate",
        render(item) {
          return <span>{item.AccountCreatedDate ? dateTimeUtils.formatDate(item.AccountCreatedDate) : "-"}</span>;
        },
      },
      {
        name: "Last activity",
        width: 3,
        isSortable: true,
        sortKey: "LastEventDate",
        render(item) {
          return <span>{item.LastEventDate ? dateTimeUtils.formatDate(item.LastEventDate) : "-"}</span>;
        },
      },
    ],
    [handleAccountSelection],
  );

  if (!reportEnabled) {
    return null;
  }

  return (
    <div className="tabSpacer">
      <GenericPerformanceListWithServerPagination
        title="Most Active Customers"
        columns={columns}
        filter={filter}
        fetchPage={fetchAccountsData}
        queryKeyBase={queryKey}
        hasDataForExport={updateExportDataState}
      />
    </div>
  );
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  setIsExportEnabled: bindAction(setHasDataToExport, dispatch),
  setExport: bindAction(setExportAction, dispatch),
  accountsExport: bindActionCreators(fetchPeopleOverviewAccountsTableExport, dispatch),
});

const connector = connect(null, mapDispatchToProps);
export type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(AccountsTable);
export default ConnectedComponent;
